import z from "zod";
import { ethnicityCodes, languageLevelOptions, nationalityCodes } from "./shared";
import { languageCertificatesApi } from "@/lib";

export type tLanguageCertificateSchema = z.infer<typeof languageCertificateSchema>;

export const languageCertificateSchema = z
	.object({
		id: z.string().optional(),
		cccd: z
			.string()
			.nonempty("Bắt buộc nhập")
			.refine((v) => !v || /^(?:[a-zA-Z0-9]{8}|[a-zA-Z0-9]{9}|[a-zA-Z0-9]{12})$/.test(v), "CCCD sai định dạng"),
		studentId: z.string().nonempty("Bắt buộc nhập").max(250, { message: "Tối đa 250 ký tự" }),
		studentName: z.string().nonempty("Bắt buộc nhập").max(250, { message: "Tối đa 250 ký tự" }),
		dateOfBirth: z.date({ required_error: "Bắt buộc nhập" }).optional().refine((val) => {
			if (!val) return true;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const cutoff = new Date(today);
			cutoff.setDate(today.getDate() - 1);
			const dob = new Date(val);
			dob.setHours(0, 0, 0, 0);
			return dob <= cutoff;
		}, {
			message: "Ngày sinh không hợp lệ",
		}),
		placeOfBirth: z.string().max(250, { message: "Tối đa 250 ký tự" }).optional(),
		gender: z.string().nonempty("Bắt buộc nhập"),
		ethnicCode: z
			.string()
			.optional()
			.refine((val) => !val || ethnicityCodes.includes(val), { message: "Mã dân tộc không hợp lệ" }),
		ethnicity: z.string().optional(),
		nationalityCode: z
			.string()
			.optional()
			.refine((val) => !val || nationalityCodes.includes(val), { message: "Mã quốc tịch không hợp lệ" }),
		nationality: z.string().optional(),
		language: z.string().nonempty("Bắt buộc chọn"),
		// languageLevel: z.string().nonempty("Bắt buộc nhập"),
		// classification: z.string().nonempty("Bắt buộc nhập"),
		// languageCode: z.string().optional(),
		level: z.string().nonempty("Bắt buộc chọn"),
		// examDate: z.date().optional(),
		// issueDate: z.date().optional(),
		// expiryDate: z.date().optional(),
		// totalScore: z.coerce.number(),
		// listeningScore: z.coerce.number(),
		// speakingScore: z.coerce.number(),
		// readingScore: z.coerce.number(),
		// writingScore: z.coerce.number(),
		// certificateType: z.string(),
		// certificateTypeId: z.string(),
		signedBy: z.string().nonempty("Bắt buộc nhập"),
		signerTitle: z.string().nonempty("Bắt buộc nhập"),
		signDate: z.date({ required_error: "Bắt buộc nhập" }).optional().refine((val) => {
			if (!val) return true;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const cutoff = new Date(today);
			cutoff.setDate(today.getDate() - 1);
			const dob = new Date(val);
			dob.setHours(0, 0, 0, 0);
			return dob <= cutoff;
		}, {
			message: "Ngày ký không hợp lệ",
		}),
		notes: z.string().max(500, { message: "Tối đa 500 ký tự" }).optional(),
		// status: z.string().optional(),
		course: z.string().max(250, { message: "Tối đa 250 ký tự" }).nonempty("Bắt buộc nhập"),
		courseStart: z.date({ invalid_type_error: "Không hợp lệ" }).optional(),
		courseEnd: z.date({ invalid_type_error: "Không hợp lệ" }).optional(),
		examCenter: z.string().max(250, { message: "Tối đa 250 ký tự" }).nonempty("Bắt buộc nhập"),
		// classificationId: z.string().optional(),
		requestTypeId: z.string().nonempty("Bắt buộc chọn"),
		requestType: z.string().optional(),
		currentCertificateNumber: z.string().optional(),
		certificateNumber: z.string().max(250, { message: "Tối đa 250 ký tự" }).nonempty("Bắt buộc nhập"), //= Số hiệu bằng
		registrationNumber: z.string().max(50, { message: "Tối đa 50 ký tự" }).nonempty("Bắt buộc nhập"), //= Số vào sổ
		recognitionDecisionNumber: z.string().max(250, { message: "Tối đa 250 ký tự" }).nonempty("Bắt buộc nhập"), //= Số quyết định công nhận
		decisionToEstablishCouncilNumber: z.string().max(250, { message: "Tối đa 250 ký tự" }).nonempty("Bắt buộc nhập"), //= Quyết định thành lập hội đồng
	})
	.superRefine(async (data, ctx) => {
		const { certificateNumber, currentCertificateNumber, courseStart, courseEnd, dateOfBirth, signDate } = data;

		if (!dateOfBirth) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Bắt buộc nhập",
				path: ["dateOfBirth"],
			});
		}

		if (!signDate) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Bắt buộc nhập",
				path: ["signDate"],
			});
		}

		if (courseStart && courseEnd) {
			if (courseEnd < courseStart) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Ngày kết thúc khoá học phải sau ngày bắt đầu khoá học",
					path: ["courseEnd"],
				});
			}
		}

		if (!certificateNumber) return;
		if (currentCertificateNumber && certificateNumber == currentCertificateNumber) {
			return; // Success (bỏ qua check API)
		}

		// Nếu giá trị ĐÃ thay đổi hoặc là tạo mới (không có currentCertificateNumber), ta mới check API
		const exists = await checkDegreeNumberExists(certificateNumber);

		// Logic check: Nếu exists là TRUE (đã tồn tại), thì THẤT BẠI
		if (exists) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Số hiệu bằng đã tồn tại",
				path: ["certificateNumber"], // Chỉ định lỗi vào trường nào
			});
			return; // Validation thất bại
		}


	});

async function checkDegreeNumberExists(certificateNumber: string): Promise<boolean> {
	const response = await languageCertificatesApi.checkExist({ searchValue: certificateNumber, field: "degreeNumber" });
	return response as unknown as boolean;
}

const regexNoSpecial = /^[A-Za-z0-9]+$/;
const regexDate = /^(\d{2})\/(\d{2})\/(\d{4})$/;
export function validateLanguageCertificateImportData(importedCertificates: any[]) {

	const studentIdCounts = {} as any;
	const certificateCounts = {} as any;
	const certificateNumberCounts = {} as any;

	importedCertificates.forEach((item: any) => {
		studentIdCounts[item.studentId] = (studentIdCounts[item.studentId] || 0) + 1;
		certificateCounts[item.recognitionDecisionNumber] = (certificateCounts[item.recognitionDecisionNumber] || 0) + 1;
		certificateNumberCounts[item.certificateNumber] = (certificateNumberCounts[item.certificateNumber] || 0) + 1;
	});


	importedCertificates.forEach((item) => {
		const errors: string[] = [];

		// ===== CCCD =====
		if (item.cccd) {
			if (!/^(?:[a-zA-Z0-9]{8}|[a-zA-Z0-9]{9}|[a-zA-Z0-9]{12})$/.test(item.cccd)) {
				errors.push("CCCD sai định dạng");
			}
		} else {
			errors.push("CCCD không được để trống");
		}

		// ===== Mã sinh viên =====
		if (item.studentId) {
			if (!item.studentId) {
				errors.push("Mã sinh viên không được để trống");
			} else {
				if (item.studentId.length > 250) errors.push("Mã sinh viên tối đa 250 ký tự");
				if (!regexNoSpecial.test(item.studentId)) {
					errors.push("Mã sinh viên không chứa khoảng trắng hoặc ký tự đặc biệt");
				}
				if (studentIdCounts[item.studentId] > 1) {
					errors.push("Mã sinh viên phải duy nhất trong file");
				}
			}
		} else {
			errors.push("Mã sinh viên không được để trống");
		}

		// ===== Họ tên =====
		if (!item.studentName) {
			errors.push("Họ tên không được để trống");
		} else if (item.studentName.length > 250) {
			errors.push("Họ tên không hợp lệ (Tối đa 250 ký tự)");
		}

		// ===== Ngày sinh =====
		if (!item.dateOfBirth) {
			errors.push("Ngày sinh không được để trống");
		} else {
			if (!regexDate.test(item.dateOfBirth)) {
				errors.push("Ngày sinh phải dạng dd/mm/yyyy");
			} else {
				// So sánh theo ngày (bỏ qua giờ/phút) để tránh lệch múi giờ
				const [dd, mm, yyyy] = item.dateOfBirth.split("/").map(Number);
				const dob = new Date(yyyy, (mm || 1) - 1, dd || 1);
				dob.setHours(0, 0, 0, 0);
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				if (dob >= today) {
					errors.push("Ngày sinh không hợp lệ");
				}
			}
		}

		// ===== Ngày khóa học bắt đầu =====
		if (item.courseStart) {
			if (!regexDate.test(item.courseStart)) {
				errors.push("Ngày khóa học bắt đầu phải dạng dd/mm/yyyy");
			}
		}
		// ===== Ngày khóa học kết thúc =====
		if (item.courseEnd) {
			if (!regexDate.test(item.courseEnd)) {
				errors.push("Ngày khóa học kết thúc phải dạng dd/mm/yyyy");
			}
		}

		// course start phải trước course end
		if (item.courseStart && item.courseEnd) {
			const [dd1, mm1, yyyy1] = item.courseStart.split("/").map(Number);
			const courseStartDate = new Date(yyyy1, (mm1 || 1) - 1, dd1 || 1);
			courseStartDate.setHours(0, 0, 0, 0);
			const [dd2, mm2, yyyy2] = item.courseEnd.split("/").map(Number);
			const courseEndDate = new Date(yyyy2, (mm2 || 1) - 1, dd2 || 1);
			courseEndDate.setHours(0, 0, 0, 0);

			if (courseEndDate < courseStartDate) {
				errors.push("Ngày kết thúc khoá học phải sau ngày bắt đầu khoá học");
			}
		}

		// ===== Nơi sinh =====
		if (item.placeOfBirth && item.placeOfBirth.length > 250) {
			errors.push("Nơi sinh không hợp lệ (Tối đa 250 ký tự)");
		}

		// ===== Giới tính =====
		if (!item.gender) {
			errors.push("Giới tính không được để trống");
		} else {
			const validGenders = ["Nam", "Nữ"];
			if (!validGenders.includes(item.gender)) {
				errors.push("Giới tính không hợp lệ");
			}
		}

		// ===== Dân tộc =====
		if (item.ethnicCode && !ethnicityCodes.includes(item.ethnicCode)) {
			errors.push("Mã dân tộc không hợp lệ");
		}

		// ===== Quốc tịch =====
		if (item.nationalityCode && !nationalityCodes.includes(item.nationalityCode)) {
			errors.push("Mã quốc tịch không hợp lệ");
		}

		// ===== Ngoại ngữ =====
		if (!item.language) {
			errors.push("Ngoại ngữ không được để trống");
		}

		// ===== Trình độ =====
		if (!item.level) {
			errors.push("Trình độ không được để trống");
		} else {
			const validLevels = languageLevelOptions.map((opt) => opt.value);
			if (!validLevels.includes(item.level)) {
				errors.push("Trình độ không hợp lệ");
			}
		}

		// ===== Người ký =====
		if (!item.signedBy) {
			errors.push("Người ký không được để trống");
		}

		// ===== Chức danh người ký =====
		if (!item.signerTitle) {
			errors.push("Chức danh người ký không được để trống");
		}

		// ===== Ngày ký =====
		if (!item.signerDate) {
			errors.push("Ngày ký không được để trống");
		} else {
			if (!regexDate.test(item.signerDate)) {
				errors.push("Ngày ký phải dạng dd/mm/yyyy");
			} else {
				// So sánh theo ngày (bỏ qua giờ/phút) để tránh lệch múi giờ
				const [dd, mm, yyyy] = item.signerDate.split("/").map(Number);
				const dob = new Date(yyyy, (mm || 1) - 1, dd || 1);
				dob.setHours(0, 0, 0, 0);
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				if (dob >= today) {
					errors.push("Ngày ký không hợp lệ");
				}
			}
		}

		// ===== Ghi chú =====
		if (item.notes && item.notes.length > 500) {
			errors.push("Ghi chú không hợp lệ (Tối đa 500 ký tự)");
		}

		// ===== Khoá học =====
		if (!item.course) {
			errors.push("Khoá học không được để trống");
		} else if (item.course.length > 250) {
			errors.push("Khoá học không hợp lệ (Tối đa 250 ký tự)");
		}

		// ===== Hội đồng thi =====
		if (!item.examCenter) {
			errors.push("Hội đồng thi không được để trống");
		} else if (item.examCenter.length > 250) {
			errors.push("Hội đồng thi không hợp lệ (Tối đa 250 ký tự)");
		}

		// ===== Loại yêu cầu =====
		if (!item.requestTypeId) {
			errors.push("Loại yêu cầu không được để trống");
		}

		// ===== Số hiệu bằng =====
		if (!item.certificateNumber) {
			errors.push("Số hiệu bằng không được để trống");
		} else if (item.certificateNumber.length > 250) {
			errors.push("Số hiệu bằng không hợp lệ (Tối đa 250 ký tự)");
		}
		if (certificateNumberCounts[item.certificateNumber] > 1) {
			errors.push("Số hiệu bằng phải duy nhất trong file");
		}

		// ===== Số vào sổ =====
		if (!item.registrationNumber) {
			errors.push("Số vào sổ không được để trống");
		} else if (item.registrationNumber.length > 50) {
			errors.push("Số vào sổ không hợp lệ (Tối đa 50 ký tự)");
		}

		// ===== Số quyết định công nhận =====
		if (!item.recognitionDecisionNumber) {
			errors.push("Số quyết định công nhận không được để trống");
		} else if (item.recognitionDecisionNumber.length > 250) {
			errors.push(" Số quyết định công nhận không hợp lệ (Tối đa 250 ký tự)");
		}

		if (certificateCounts[item.recognitionDecisionNumber] > 1) {
			errors.push("Số quyết định phải duy nhất trong file");
		}

		// ===== Quyết định thành lập hội đồng =====
		if (!item.decisionToEstablishCouncilNumber) {
			errors.push("Quyết định thành lập hội đồng không được để trống");
		} else if (item.decisionToEstablishCouncilNumber.length > 250) {
			errors.push("Quyết định thành lập hội đồng không hợp lệ (Tối đa 250 ký tự)");
		}

		item.errors = errors;
	});

	return importedCertificates;
}


export async function validateLanguageCertificateImportDataInDB(importedScholarship: any[]) {

	// for (const item of importedScholarship) {
	// 	if (!item.errors) {
	// 		item.errors = [];
	// 	}
	// 	const degreeNumberExisting = await languageCertificatesApi.checkExist({ searchValue: item.certificateNumber, field: "degreeNumber" });
	// 	if (degreeNumberExisting) {
	// 		item.errors.push("Số hiệu bằng đã tồn tại");
	// 	}
	// }
	return importedScholarship;
}